import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Copy,
  Bank,
  CreditCard,
  CheckCircle,
  Receipt,
  DownloadSimple,
  Trash,
  MoneyWavy,
  Info,
  Lightning,
  Clock,
  X,
} from "@phosphor-icons/react";
import { generatePaymentReceiptPDF, generatePaymentBatchPDF } from "../../lib/paymentReceipt";
import AccountStatusCard from "../../components/payments/AccountStatusCard";

const METHODS = [
  { key: "transferencia", label: "Transferencia", icon: Bank },
  { key: "tarjeta_debito", label: "Tarjeta de Débito", icon: CreditCard },
];

const METHOD_LABEL = {
  transferencia: "Transferencia",
  tarjeta_debito: "Tarjeta de Débito",
  mercado_pago: "Mercado Pago",
  efectivo: "Efectivo",
};

const STATUS_META = {
  aprobado: { label: "Aprobado", color: "text-[#32D74B]", bg: "bg-[#32D74B]/15 border-[#32D74B]/40", icon: CheckCircle },
  registrado: { label: "Registrado", color: "text-[#32D74B]", bg: "bg-[#32D74B]/15 border-[#32D74B]/40", icon: CheckCircle },
  pendiente: { label: "Pendiente de confirmación", color: "text-[#FFD60A]", bg: "bg-[#FFD60A]/10 border-[#FFD60A]/40", icon: Clock },
  rechazado: { label: "Rechazado", color: "text-[#FF453A]", bg: "bg-[#FF453A]/10 border-[#FF453A]/40", icon: X },
};

function CopyButton({ text, label }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copiado al portapapeles`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("No se pudo copiar");
    }
  };
  return (
    <button type="button" onClick={handleCopy} className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5 flex-shrink-0" data-testid={`copy-${label.toLowerCase()}`}>
      {copied ? <CheckCircle size={12} weight="fill" className="text-[#32D74B]" /> : <Copy size={12} weight="bold" />}
      {copied ? "Copiado" : "Copiar"}
    </button>
  );
}

export default function PaymentsTab({ userId, targetUser }) {
  const today = new Date().toISOString().slice(0, 10);
  const [bank, setBank] = useState(null);
  const [mpConfig, setMpConfig] = useState(null);
  const [payments, setPayments] = useState([]);
  const [accountStatus, setAccountStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    amount: "",
    method: "transferencia",
    concept: "Mensualidad",
    reference: "",
    payment_date: today,
  });
  const [submitting, setSubmitting] = useState(false);
  const [mpLaunching, setMpLaunching] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    setLoading(true);
    try {
      const [b, mp, p, s] = await Promise.all([
        api.get("/payments/bank-details"),
        api.get("/payments/mp-config"),
        api.get(`/users/${userId}/payments`),
        api.get(`/users/${userId}/account-status`).catch(() => ({ data: null })),
      ]);
      setBank(b.data);
      setMpConfig(mp.data);
      setPayments(p.data);
      setAccountStatus(s.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [userId]); // eslint-disable-line

  // Toast de retorno desde Mercado Pago
  useEffect(() => {
    const status = searchParams.get("mp_status");
    if (!status) return;
    if (status === "success") toast.success("Mercado Pago confirmó el pago. El comprobante se aprobará en segundos.");
    else if (status === "pending") toast.info("Tu pago está pendiente. Te avisamos cuando se acredite.");
    else if (status === "failure") toast.error("Mercado Pago rechazó el pago. Probá de nuevo o usá otro medio.");
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const startMercadoPago = async () => {
    // Para que se abra la APP nativa de Mercado Pago (iOS Universal Link /
    // Android App Link), tenemos que navegar en la misma ventana. Los popups
    // no disparan deep links — el sistema operativo sólo lo intercepta cuando
    // el navegador hace una navegación top-level.
    //
    // El back_url de MP nos trae de vuelta a Athlon. El PDF del recibo queda
    // disponible en la lista de comprobantes (botón "PDF" en cada fila).
    setMpLaunching(true);
    try {
      const payload = {
        amount: mpConfig?.monthly_fee || undefined,
        concept: `Mensualidad ${new Date().toLocaleString("es-AR", { month: "long", year: "numeric" })}`,
      };
      const { data } = await api.post(`/users/${userId}/payments/start-mp`, payload);
      const mpUrl = data.pagalink_url;

      if (!mpUrl) {
        toast.error("No se pudo generar el link de pago. Intentá de nuevo.");
        setMpLaunching(false);
        return;
      }

      toast.success("Abriendo Mercado Pago...");
      // Pequeño delay para que el toast alcance a renderizarse antes de
      // perder el contexto de la página.
      setTimeout(() => {
        window.location.href = mpUrl;
      }, 80);
    } catch (err) {
      toast.error(formatApiError(err));
      setMpLaunching(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const amount = Number(form.amount);
    if (!amount || amount <= 0) {
      toast.error("Ingresá un monto válido");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/users/${userId}/payments`, {
        amount,
        method: form.method,
        concept: form.concept || null,
        reference: form.reference || null,
        payment_date: form.payment_date,
      });
      toast.success(`Pago registrado · Comprobante ${data.receipt_number}`);
      try { await generatePaymentReceiptPDF(data, targetUser); } catch (err) { console.error(err); }
      setForm({ ...form, amount: "", reference: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const download = async (p) => {
    try { await generatePaymentReceiptPDF(p, targetUser); }
    catch (err) { toast.error("No se pudo generar el PDF"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar el comprobante ${p.receipt_number}?`)) return;
    try {
      await api.delete(`/payments/${p.payment_id}`);
      toast.success("Eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const monthlyFee = mpConfig?.monthly_fee;

  return (
    <div className="space-y-8">
      {/* Estado de cuenta (arriba de todo) */}
      {accountStatus && <AccountStatusCard status={accountStatus} />}

      {/* Pago con Mercado Pago (primary CTA) */}
      {mpConfig?.enabled && (
        <div
          className="card-base p-6 border-2 relative overflow-hidden"
          style={{ borderColor: "#009EE3" }}
          data-testid="mp-card"
        >
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-10" style={{ background: "#009EE3" }} />
          <div className="flex items-center gap-2 mb-4">
            <Lightning size={22} weight="fill" className="text-[#009EE3]" />
            <h2 className="font-display font-black uppercase text-xl tracking-tighter">Pagar con Mercado Pago</h2>
            <span className="ml-2 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-[#009EE3] text-white">
              Recomendado
            </span>
          </div>

          <p className="text-sm text-muted mb-5 leading-relaxed">
            {mpConfig.api_enabled
              ? "Un solo click. Te abre la app de Mercado Pago (o el navegador si no la tenés instalada). Apenas confirmes el pago, queda aprobado al instante."
              : "Un solo click. Te abre la app de Mercado Pago (o el navegador si no la tenés instalada). Apenas tu entrenador confirme el ingreso, el comprobante queda oficialmente aprobado."}
          </p>

          {monthlyFee ? (
            <div className="bg-[#0A0A0A] border border-app p-4 mb-5" data-testid="mp-fee">
              <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Tu cuota mensual</div>
              <div className="font-display font-black text-4xl tracking-tight text-[#009EE3]">
                ${monthlyFee.toLocaleString("es-AR")}
              </div>
            </div>
          ) : (
            <div className="bg-[#FFD60A]/10 border border-[#FFD60A]/40 p-3 mb-5 text-xs text-[#FFD60A] flex items-start gap-2">
              <Info size={14} weight="duotone" className="mt-0.5 flex-shrink-0" />
              <span>Tu entrenador todavía no configuró tu cuota mensual. Vas a poder elegir el monto dentro de Mercado Pago.</span>
            </div>
          )}

          <button
            type="button"
            onClick={startMercadoPago}
            disabled={mpLaunching}
            className="w-full font-display uppercase font-black tracking-widest text-sm text-white py-4 px-6 flex items-center justify-center gap-3 transition-transform hover:scale-[1.01] disabled:opacity-50"
            style={{ background: "#009EE3" }}
            data-testid="pay-mp-btn"
          >
            <Lightning size={20} weight="fill" />
            {mpLaunching ? "Generando..." : "Pagar con Mercado Pago"}
          </button>

          <button
            type="button"
            onClick={() => setShowManual(!showManual)}
            className="mt-3 text-[11px] uppercase tracking-widest text-muted hover:text-white"
            data-testid="toggle-manual"
          >
            {showManual ? "Ocultar pago manual" : "¿Pagaste de otra forma? Registrar pago manual"}
          </button>
        </div>
      )}

      {/* Datos bancarios */}
      {bank && (
        <div className="card-base p-6" data-testid="bank-card">
          <div className="flex items-center gap-2 mb-4">
            <Bank size={20} weight="duotone" className="text-primary-app" />
            <h2 className="font-display font-black uppercase text-xl tracking-tighter">Datos bancarios</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-[#0A0A0A] border border-app p-4" data-testid="bank-alias">
              <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Alias</div>
              <div className="flex items-center gap-3 justify-between flex-wrap">
                <div className="font-display font-black text-2xl tracking-tight break-all">{bank.alias}</div>
                <CopyButton text={bank.alias} label="Alias" />
              </div>
            </div>
            <div className="bg-[#0A0A0A] border border-app p-4" data-testid="bank-cbu">
              <div className="text-[10px] uppercase tracking-widest text-muted mb-1">CBU</div>
              <div className="flex items-center gap-3 justify-between flex-wrap">
                <div className="font-display font-black text-xl tracking-tight break-all">{bank.cbu}</div>
                <CopyButton text={bank.cbu} label="CBU" />
              </div>
            </div>
          </div>
          <div className="mt-3 text-[11px] text-muted flex items-start gap-2">
            <Info size={14} weight="duotone" className="mt-0.5 flex-shrink-0" />
            <span>Titular: <strong className="text-white">{bank.titular}</strong>.</span>
          </div>
        </div>
      )}

      {/* Formulario manual (oculto por defecto si MP está activo) */}
      {(!mpConfig?.enabled || showManual) && (
        <div className="card-base p-6">
          <div className="flex items-center gap-2 mb-4">
            <MoneyWavy size={20} weight="duotone" className="text-primary-app" />
            <h2 className="font-display font-black uppercase text-xl tracking-tighter">Registrar pago manual</h2>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="payment-form">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted mb-2">Método</div>
              <div className="flex gap-3 flex-wrap">
                {METHODS.map((m) => {
                  const Icon = m.icon;
                  const active = form.method === m.key;
                  return (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => setForm({ ...form, method: m.key })}
                      className={`flex items-center gap-2 text-xs uppercase tracking-widest font-bold border px-4 py-3 transition-colors ${
                        active ? "border-[#FF3B30] bg-[#FF3B30] text-white" : "border-[#262626] text-muted hover:text-white"
                      }`}
                      data-testid={`method-${m.key}`}
                    >
                      <Icon size={16} weight="bold" /> {m.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-muted block mb-2">Monto (ARS)</label>
                <input
                  type="number" min="1" step="0.01" className="input-base"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  required placeholder="15000" data-testid="payment-amount"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted block mb-2">Fecha del pago</label>
                <input
                  type="date" className="input-base"
                  value={form.payment_date}
                  onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                  data-testid="payment-date"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted block mb-2">Concepto</label>
                <input
                  className="input-base"
                  value={form.concept}
                  onChange={(e) => setForm({ ...form, concept: e.target.value })}
                  placeholder="Mensualidad Abril" data-testid="payment-concept"
                />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-muted block mb-2">
                {form.method === "transferencia"
                  ? "Nº de transferencia / operación (opcional)"
                  : "Últimos 4 dígitos (opcional)"}
              </label>
              <input
                className="input-base"
                value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                placeholder={
                  form.method === "transferencia" ? "Ej: 12345678" : "Ej: **** 1234"
                }
                data-testid="payment-reference"
              />
            </div>

            <button type="submit" disabled={submitting} className="btn-primary flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="submit-payment">
              <Receipt size={16} weight="bold" />
              {submitting ? "Generando comprobante..." : "Registrar y descargar comprobante"}
            </button>
          </form>
        </div>
      )}

      {/* Historial de recibos */}
      <div className="card-base p-6" data-testid="receipts-section">
        <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Receipt size={20} weight="duotone" className="text-primary-app" />
            <h2 className="font-display font-black uppercase text-xl tracking-tighter">Mis comprobantes</h2>
            {payments.length > 0 && (
              <span className="text-[10px] uppercase tracking-widest text-muted ml-2">· {payments.length}</span>
            )}
          </div>
          {payments.length >= 2 && (
            <button
              onClick={async () => {
                try {
                  await generatePaymentBatchPDF(payments, targetUser, { title: "Historial" });
                  toast.success(`PDF consolidado con ${payments.length} comprobantes`);
                } catch (err) {
                  toast.error(err?.message || "No se pudo generar el PDF");
                }
              }}
              className="btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1"
              data-testid="download-all-receipts-btn"
              title="Un solo PDF con todos los comprobantes de este atleta"
            >
              <DownloadSimple size={14} weight="bold" /> Descargar todos
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-sm text-muted text-center py-6">Cargando...</div>
        ) : payments.length === 0 ? (
          <div className="text-sm text-muted text-center py-10">
            Todavía no registraste ningún pago.<br />
            Cuando lo hagas, tus comprobantes aparecerán acá.
          </div>
        ) : (
          <div className="space-y-2" data-testid="receipts-list">
            {payments.map((p) => {
              const st = STATUS_META[p.status] || STATUS_META.registrado;
              const StIcon = st.icon;
              return (
                <div key={p.payment_id} className="bg-[#0A0A0A] border border-app p-4 flex items-center gap-4 flex-wrap" data-testid={`receipt-row-${p.payment_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-display font-black tracking-tight text-lg">{p.receipt_number}</span>
                      <span className="text-[9px] uppercase tracking-widest font-bold bg-[#262626] text-muted px-1.5 py-0.5">
                        {METHOD_LABEL[p.method] || p.method}
                      </span>
                      <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${st.bg} ${st.color} flex items-center gap-1`}>
                        <StIcon size={10} weight="fill" /> {st.label}
                      </span>
                    </div>
                    <div className="text-sm text-muted">
                      {p.concept} · {p.payment_date}
                      {p.reference && <> · Ref: {p.reference}</>}
                    </div>
                  </div>
                  <div className="font-display font-black text-2xl text-primary-app">
                    ${p.amount.toLocaleString("es-AR")}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => download(p)} className="btn-secondary !py-2 !px-3 text-xs flex items-center gap-1" data-testid={`download-receipt-${p.payment_id}`}>
                      <DownloadSimple size={14} weight="bold" /> PDF
                    </button>
                    <button onClick={() => remove(p)} className="btn-secondary !py-2 !px-3 hover:!border-[#FF3B30] hover:text-[#FF3B30]" data-testid={`delete-receipt-${p.payment_id}`}>
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
